'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';

function triggerHaptic() {
  try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(20); } catch { /* ignore */ }
}

export default function LucatButton() {
  const router = useRouter();
  const [state, setState] = useState<'idle' | 'pressed' | 'loading'>('idle');

  const handlePress = useCallback(() => {
    if (state !== 'idle') return;
    triggerHaptic();
    setState('pressed');
    setTimeout(() => {
      setState('loading');
      setTimeout(() => {
        router?.push('/voice-assistant-screen-parler-lucat');
      }, 80);
    }, 150);
  }, [state, router]);

  const isPressed = state === 'pressed';
  const isLoading = state === 'loading';

  return (
    <>
      <button
        onClick={handlePress}
        aria-label="Parler à LUCAT — assistant vocal — touchez ici pour parler"
        aria-busy={isLoading}
        disabled={state !== 'idle'}
        data-pressed={isPressed ? 'true' : undefined}
        style={{
          width: '100%',
          minHeight: '96px',
          background: isPressed || isLoading
            ? 'linear-gradient(135deg, #3D9A10 0%, #2D7A08 100%)'
            : 'linear-gradient(135deg, #5DBB1F 0%, #3D9A10 100%)',
          borderRadius: '16px',
          border: isPressed ? '2px solid #2D7A08' : '2px solid transparent',
          cursor: state !== 'idle' ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '18px',
          boxShadow: isPressed || isLoading
            ? '0 2px 8px rgba(93,187,31,0.3)'
            : '0 4px 24px rgba(93,187,31,0.45)',
          transform: isPressed ? 'translateY(1px) scale(0.97)' : 'scale(1)',
          filter: isPressed ? 'brightness(0.92)' : 'none',
          transition: 'transform 120ms ease, background 120ms ease, box-shadow 120ms ease, border-color 120ms ease, filter 120ms ease',
          flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {/* Mic icon circle */}
        <div style={{
          width: '60px', height: '60px',
          background: 'rgba(255,255,255,0.22)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {isLoading ? (
            <div style={{
              width: '30px', height: '30px', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.8)',
              borderTopColor: 'transparent',
              animation: 'sab-spin 0.7s linear infinite',
            }} aria-hidden="true" />
          ) : (
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
          <span style={{ color: '#FFFFFF', fontSize: '28px', fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {isLoading ? 'Ouverture du micro…' : 'Parler à LUCAT'}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '18px', fontWeight: 500, whiteSpace: 'nowrap', marginTop: '2px' }}>
            {isLoading ? 'Préparation en cours…' : 'Touchez ici pour parler'}
          </span>
        </div>
        {/* Sound waves — hidden during loading */}
        {!isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: 'auto', paddingRight: '10px' }}>
            {[16, 26, 36, 26, 16]?.map((h, i) => (
              <div key={`wave-${i}`} style={{ width: '5px', height: `${h}px`, background: 'rgba(255,255,255,0.55)', borderRadius: '3px' }} />
            ))}
          </div>
        )}
      </button>
      <style>{`@keyframes sab-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}